/**
 * The Altar redesign (2026-10-27): the pure parts of the first-visit rite and
 * the everyday Altar. Design: Claude outputs/altar-redesign-2026-10-27/
 * (DESIGN.md, prototype.html).
 *
 * The rite is sky → intention → painting. The sky writes itself in one
 * sentence at a time, Mystic AIda offers an intention drawn from it (the
 * server seeds it from today's horoscope), and "Make the space yours" paints
 * the room, pre-chosen from what they already do and their signs.
 */
import { MONTHS, SIGNS, GLYPHS } from '@/utils/birth-ask'

// AltarBuilder.vue SUGGEST.paths / SUGGEST.mood (the real lists).
export const ALTAR_PATHS = Object.freeze(['Tarot', 'Astrology', 'Buddhism', 'Christianity', 'Kabbalah', 'Hermeticism',
  'Wicca', 'Heathenry', 'Hoodoo', 'Hinduism', 'Sufism', 'Stoicism', 'Animism', 'Ancestral veneration', 'Shamanism',
  'Taoism', 'Paganism', 'Secular / spiritual'])
export const ALTAR_MOODS = Object.freeze(['candlelit and intimate', 'cosmic and vast', 'forest grove',
  'serene and minimal', 'oceanic and deep', 'ancient temple', 'cathedral light', 'starlit night'])
/**
 * The paint form's live preview (2026-10-30, Claude outputs/altar-paint-form-2026-10-30/):
 * each mood's drawn scene (AltarScenes) and the room's light
 * [scene, sky, ground, light, accent]. '' (no mood) is the plain night.
 */
export const MOOD_LOOK = Object.freeze({
  'candlelit and intimate': ['candle', '#3a1708', '#120610', '#ffb347', '#e9a65a'],
  'cosmic and vast': ['cosmic', '#1d0b48', '#05030f', '#a08bff', '#ff7bd1'],
  'forest grove': ['forest', '#123a2c', '#040d08', '#b9e08f', '#e6d28a'],
  'serene and minimal': ['serene', '#4a4270', '#1a1628', '#efe9ff', '#cfc4ea'],
  'oceanic and deep': ['ocean', '#0a3a6b', '#020a18', '#6fd6ea', '#9fe8f0'],
  'ancient temple': ['temple', '#5a3016', '#140a04', '#ffcf8a', '#c98d4a'],
  'cathedral light': ['cath', '#2c2148', '#0b0814', '#fff1c9', '#9ec5ff'],
  'starlit night': ['star', '#13245a', '#03050f', '#cfd9ff', '#f0d9a3'],
  '': ['none', '#1f103e', '#0d0520', '#f0d9a3', '#9b86c9'],
})
export const ALTAR_STYLES = Object.freeze(['Illustrated', 'Animated', 'Watercolor', 'Storybook', 'Stained glass',
  'Ethereal', 'Realistic'])
/** What an empty style paints as on the server (altar_space.py DEFAULT_STYLE). */
export const DEFAULT_STYLE = 'Illustrated'
/** Paths shown before "More paths". */
export const PATHS_SHOWN = 8

export const RITE_COPY = Object.freeze({
  heroEyebrow: 'The Moon',
  heroTitle: 'Your Altar',
  heroP1: 'Your practice lives here now. Each morning brings your own sky, read from the chart you were born under.',
  heroP2: 'Hold an intention through the day, in a space painted from what you believe.',
  heroQuiet: 'Your Mirror and your Journal live here too.',
  getStarted: 'Get started',
  skyTitle: 'Your sky this morning',
  skyReading: 'Reading your sky…',
  tapAll: 'Tap to show it all',
  // The fallback for a member with no birthday: a quiet line, not a box.
  noBirthday: 'Your sky arrives here once your birthday is with us. Your Mirror holds it.',
  toIntent: 'Set an intention from it',
  offer: 'From your sky this morning, I’d hold this:',
  offerNoSky: 'For today, I’d hold this:',
  offering: 'Finding your words…',
  keep: 'Keep it',
  rewrite: 'Rewrite it',
  another: 'Offer another',
  ownTitle: 'Your intention, in your own words',
  ownHint: 'As few words as you like. They stay exactly as you write them.',
  keepMine: 'Keep my words',
  backToOffer: 'Back to Mystic AIda’s',
  paintEyebrow: 'Your Altar',
  paintTitle: 'Make the space yours',
  paintLede: 'What you believe, and the sky you were born under, painted into one place.',
  pathsLabel: 'The paths you walk',
  morePaths: 'More paths',
  moodLabel: 'Where it feels like home',
  wordsLabel: 'In your own words',
  wordsIf: 'if you like',
  wordsPlaceholder: 'What you hold sacred, in a line or two',
  styleLabel: 'Art style',
  paintNow: 'Paint my Altar',
  paintLater: 'Paint it later',
  painting: 'Painting your Altar',
  paintFailed: 'The painting didn’t take this time. Your Altar is still here; try again.',
  closing: 'A new sky arrives each morning.',
  invite: '✦ Paint your Altar',
  inviteSub: 'What you believe, painted into this space.',
  // The intention sheet (tap the intention).
  sheetEyebrow: 'Your intention',
  holdOn: 'Keep holding it',
  renew: 'Draw a new one from today’s sky',
  writeOwn: 'Write my own',
  // The magnifier on the painting (the horoscope's tune form is HoroscopeTuneForm).
  fullSize: 'See my Altar full size',
  cancel: 'Cancel',
  shapeMore: 'Shape it further with Mystic AIda',
  surprise: 'Surprise me',
  skyEyebrow: 'Your sky',
  skyEyebrowWhen: 'this morning',
})

/** The horoscope, one sentence a line (the sky writes itself in). */
export function skyLines (text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim()
  if (!t) return []
  const out = t.match(/[^.!?…]+(?:[.!?…]+[”’"')\]]*|$)/g) || [t]
  return out.map((s) => s.trim()).filter(Boolean)
}

const ELEMENT = { Aries: 'fire', Leo: 'fire', Sagittarius: 'fire', Taurus: 'earth', Virgo: 'earth', Capricorn: 'earth',
  Gemini: 'air', Libra: 'air', Aquarius: 'air', Cancer: 'water', Scorpio: 'water', Pisces: 'water' }
const MOOD_OF = { water: 'oceanic and deep', fire: 'candlelit and intimate', earth: 'forest grove', air: 'cosmic and vast' }

function known (chart) {
  const c = chart || {}
  return ['sun', 'moon'].map((k) => c[k]).filter((s) => SIGNS.includes(s))
}

/** "Your Pisces Sun and Cancer Moon are painted in." Only what the chart holds. */
export function signsPaintedLine (chart) {
  const c = chart || {}
  const sun = SIGNS.includes(c.sun) ? c.sun : ''
  const moon = SIGNS.includes(c.moon) ? c.moon : ''
  if (sun && moon) return `Your ${sun} Sun and ${moon} Moon are painted in.`
  if (sun) return `Your ${sun} Sun is painted in.`
  if (moon) return `Your ${moon} Moon is painted in.`
  return ''
}

/** Text-style glyphs (VS15) for the Sun and Moon the chart holds. */
export function signGlyphs (chart) {
  return known(chart).map((s) => GLYPHS[SIGNS.indexOf(s)])
}

/** The atmosphere pre-chosen from the element their Sun and Moon share (the Sun wins a tie). */
export function moodForSigns (chart) {
  const els = known(chart).map((s) => ELEMENT[s])
  if (!els.length) return ''
  const count = (e) => els.filter((x) => x === e).length
  const el = els.slice().sort((a, b) => count(b) - count(a))[0]
  return MOOD_OF[el] || ''
}

/** The paths pre-chosen: what they already chose, else what they do here. */
export function defaultPaths (facets) {
  const p = facets && Array.isArray(facets.paths) ? facets.paths.filter(Boolean) : []
  return p.length ? p : ['Tarot', 'Astrology']
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** "Thursday 30 October" from a YYYY-MM-DD (read as a local calendar date). */
export function dateEyebrow (iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  if (!m) return ''
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`
}

const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** "Your intention, set this morning" / "held since Thursday" / "held since 12 October". */
export function heldSince (createdAt, now = new Date()) {
  const t = createdAt ? new Date(createdAt) : null
  if (!t || isNaN(t.getTime())) return 'Your intention'
  const days = Math.round((dayStart(now) - dayStart(t)) / 86400000)
  if (days <= 0) return t.getHours() < 12 ? 'Your intention, set this morning' : 'Your intention, set today'
  if (days < 7) return `Your intention, held since ${WEEKDAYS[t.getDay()]}`
  return `Your intention, held since ${t.getDate()} ${MONTHS[t.getMonth()]}`
}

/** The rite runs on Home, signed in, when the server says it is due. */
export function riteDue (altarSpace, { signedIn = false, onHome = false } = {}) {
  return !!(signedIn && onHome && altarSpace && altarSpace.rite && altarSpace.rite.due)
}

/** Today in the seeker's own calendar (YYYY-MM-DD). */
export function localToday (d = new Date()) {
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** The look of a mood (unknown moods are the plain night). */
export function moodLook (mood) { return MOOD_LOOK[mood] || MOOD_LOOK[''] }

/** The id of a style's drawn treatment (AltarScenes filters): 'Stained glass' → 'stained'. */
export function styleKey (style) {
  const s = style || DEFAULT_STYLE
  return s === 'Stained glass' ? 'stained' : s.toLowerCase()
}

/** The caption under the preview: only the choices, "Tarot · Astrology" / "Candlelit and intimate · Illustrated". */
export function paintRecipe ({ paths = [], mood = '', style = '' } = {}) {
  const out = []
  if (paths.length) out.push(paths.join(' · '))
  const second = [mood && mood[0].toUpperCase() + mood.slice(1), style].filter(Boolean).join(' · ')
  if (second) out.push(second)
  return out
}

/** "Surprise me": a new mood and a new style, never the ones showing. */
export function surpriseLook (mood, style, rnd = Math.random) {
  const pick = (list, not) => {
    const l = list.filter((x) => x !== not)
    return l[Math.min(l.length - 1, Math.floor(rnd() * l.length))]
  }
  return { mood: pick(ALTAR_MOODS, mood), style: pick(ALTAR_STYLES, style || DEFAULT_STYLE) }
}
